import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, Users, MessageSquare } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import PendingEventsPanel from '../components/admin/PendingEventsPanel';
import { Navigate, Link } from 'react-router-dom';
import { adminEventService } from '../services/eventService';
import { adminUserService } from '../services/adminUserService';
import { adminGroupService } from '../services/adminGroupService';
import { LoadingSpinner } from '../components/ui';
import { toast } from '../components/ui/Toaster';
import { AdminLayout } from '../components/admin';

interface AdminStats {
  pendingCount: number;
  approvedTodayCount: number;
  rejectedTodayCount: number;
  totalPublishedCount: number;
}

interface PlatformStats {
  totalUsers: number;
  totalGroups: number;
  activeGroups: number;
  totalMessages: number;
}

const AdminDashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Redirect non-admin users
  if (!user?.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  useEffect(() => {
    loadAllStats();
  }, []);

  const loadAllStats = async () => {
    try {
      setIsLoadingStats(true);

      // Load admin event stats and platform stats in parallel
      const [adminStats, usersResponse, groupsResponse] = await Promise.all([
        adminEventService.getAdminStats(),
        adminUserService.getAllUsers({ page: 1, limit: 1 }),
        adminGroupService.getAllGroups({ page: 1, limit: 1000 }) // Get all groups to calculate stats
      ]);

      setStats(adminStats);

      // Calculate platform statistics
      const totalMessages = groupsResponse.groups.reduce((sum, group) => sum + (group.messageCount || 0), 0);
      const activeGroups = groupsResponse.groups.filter(group => group.isActive).length;

      setPlatformStats({
        totalUsers: usersResponse.pagination.total,
        totalGroups: groupsResponse.pagination.total,
        activeGroups,
        totalMessages
      });

    } catch (error: any) {
      console.error('Failed to load admin stats:', error);
      toast.error('Fout', 'Kon admin statistieken niet laden');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const refreshStats = () => {
    loadAllStats();
  };

  return (
    <AdminLayout
      title="Admin dashboard"
      subtitle="Beheer evenementen en monitor platform activiteit"
    >
      <div className="space-y-6">

      {/* Platform Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="sf-card p-6">
          <div className="flex items-center space-x-3">
            <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl bg-neon-gradient text-ink">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <p className="sf-label-mono">Totaal gebruikers</p>
              {isLoadingStats ? (
                <div className="text-2xl font-bold text-fg-bright">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <p className="text-2xl font-bold text-fg-bright">
                  {platformStats?.totalUsers ?? '-'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="sf-card p-6">
          <div className="flex items-center space-x-3">
            <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl bg-avatar-violet text-ink">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <p className="sf-label-mono">Totaal groepen</p>
              {isLoadingStats ? (
                <div className="text-2xl font-bold text-fg-bright">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <p className="text-2xl font-bold text-fg-bright">
                  {platformStats?.totalGroups ?? '-'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="sf-card p-6">
          <div className="flex items-center space-x-3">
            <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl bg-accept-gradient text-ink">
              <CheckCircle className="h-6 w-6" />
            </div>
            <div>
              <p className="sf-label-mono">Actieve groepen</p>
              {isLoadingStats ? (
                <div className="text-2xl font-bold text-fg-bright">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <p className="text-2xl font-bold text-fg-bright">
                  {platformStats?.activeGroups ?? '-'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="sf-card p-6">
          <div className="flex items-center space-x-3">
            <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl bg-avatar-pink text-ink">
              <MessageSquare className="h-6 w-6" />
            </div>
            <div>
              <p className="sf-label-mono">Totaal berichten</p>
              {isLoadingStats ? (
                <div className="text-2xl font-bold text-fg-bright">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <p className="text-2xl font-bold text-fg-bright">
                  {platformStats?.totalMessages ?? '-'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Event Management Stats */}
      {stats && (stats.pendingCount > 0 || stats.approvedTodayCount > 0 || stats.rejectedTodayCount > 0) && (
        <div className="sf-card p-6">
          <h2 className="text-lg font-bold text-fg-bright mb-4">
            Evenement beheer activiteit
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-neon-cyan/[.12]">
                <Clock className="h-5 w-5 text-neon-cyan" />
              </div>
              <div>
                <p className="sf-label-mono">Te controleren</p>
                <p className="text-xl font-bold text-fg-bright">{stats.pendingCount}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-neon-lime/[.12]">
                <CheckCircle className="h-5 w-5 text-neon-lime" />
              </div>
              <div>
                <p className="sf-label-mono">Gecontroleerd vandaag</p>
                <p className="text-xl font-bold text-fg-bright">{stats.approvedTodayCount}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-neon-pink/[.12]">
                <XCircle className="h-5 w-5 text-neon-pink-soft" />
              </div>
              <div>
                <p className="sf-label-mono">Afgewezen vandaag</p>
                <p className="text-xl font-bold text-fg-bright">{stats.rejectedTodayCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Events Panel */}
      <PendingEventsPanel onEventProcessed={refreshStats} />

      {/* Additional Admin Tools */}
      <div className="sf-card p-6">
        <h2 className="text-lg font-bold text-fg-bright mb-4">
          Admin tools
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="sf-item border border-white/[.06] p-4 transition-colors hover:border-neon-pink/30">
            <h3 className="font-semibold text-fg-bright mb-2">Evenement beheer</h3>
            <p className="text-sm text-fg-muted mb-3">
              Bekijk en beheer alle evenementen op het platform
            </p>
            <Link
              to="/admin/events"
              className="sf-link inline-flex items-center text-sm"
            >
              Beheer evenementen →
            </Link>
          </div>

          <div className="sf-item border border-white/[.06] p-4 transition-colors hover:border-neon-pink/30">
            <h3 className="font-semibold text-fg-bright mb-2">Gebruiker beheer</h3>
            <p className="text-sm text-fg-muted mb-3">
              Bekijk en beheer gebruikersaccounts en rechten
            </p>
            <Link
              to="/admin/users"
              className="sf-link inline-flex items-center text-sm"
            >
              Beheer gebruikers →
            </Link>
          </div>

          <div className="sf-item border border-white/[.06] p-4 transition-colors hover:border-neon-pink/30">
            <h3 className="font-semibold text-fg-bright mb-2">Groep beheer</h3>
            <p className="text-sm text-fg-muted mb-3">
              Monitor groepen en chat activiteit
            </p>
            <Link
              to="/admin/groups"
              className="sf-link inline-flex items-center text-sm"
            >
              Beheer groepen →
            </Link>
          </div>

          <div className="sf-item border border-white/[.06] p-4 transition-colors hover:border-neon-pink/30">
            <h3 className="font-semibold text-fg-bright mb-2">Betalingen beheer</h3>
            <p className="text-sm text-fg-muted mb-3">
              Bekijk alle premium aankopen en exporteer naar CSV
            </p>
            <Link
              to="/admin/payments"
              className="sf-link inline-flex items-center text-sm"
            >
              Beheer betalingen →
            </Link>
          </div>

          <div className="sf-item border border-white/[.06] p-4 transition-colors hover:border-neon-pink/30">
            <h3 className="font-semibold text-fg-bright mb-2">Gebruiker rapporten</h3>
            <p className="text-sm text-fg-muted mb-3">
              Bekijk en beheer gebruiker rapporten en moderatie
            </p>
            <Link
              to="/admin/reports"
              className="sf-link inline-flex items-center text-sm"
            >
              Beheer rapporten →
            </Link>
          </div>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
